import {
    arrow,
    autoUpdate,
    flip,
    offset,
    type Placement,
    shift,
    useFloating,
} from '@floating-ui/react';
import { getScrollParent, useEvent } from '@react-aria/utils';
import clsx from 'clsx';
import * as React from 'react';
import { useObjectRef } from 'react-aria';

import { assert } from '@gitbook/util-ts';

import { AnimatedStateContext } from '../animations';
import { Backdrop } from '../Backdrop';
import {
    Overlay,
    OverlayContext,
    type OverlayProps,
    useOverlayClose,
    useOverlayTriggerRef,
} from '../Overlay';
import { Portal } from '../Portal';
import { PopoverArrowContext } from './PopoverArrow';

export {
    OverlayRoot as PopoverRoot,
    OverlayTrigger as PopoverTrigger,
    useOverlayClose as usePopoverClose,
    useOverlayTriggerRef as usePopoverTriggerRef,
    useOverlayTriggerState as usePopoverTriggerState,
} from '../Overlay';

type PopoverProps = PositionedOverlayProps;

/**
 * Popover component to display content in a popover relative to a trigger.
 * Must be used within a PopoverRoot and PopoverTrigger.
 */
export const Popover = React.forwardRef(function Popover(
    props: PopoverProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const animatedState = React.useContext(AnimatedStateContext);
    const context = React.useContext(OverlayContext);
    assert(context !== null && animatedState !== null, 'Popover must be used within a PopoverRoot');

    if (animatedState === 'exited') {
        return null;
    }

    return (
        <Portal data-spine-popover>
            <PositionedOverlay ref={ref} {...props} />
        </Portal>
    );
});

type PositionedOverlayProps = {
    children: React.ReactNode;
    /** Placement of the popover relative to the trigger */
    placement?: Placement;
    /** Distance in pixels between the trigger and popover */
    offset?: number;
    /**
     * Reposition the popover relative to the anchor when the parent is scrolled.
     * Also disables the scroll-to-dismiss behaviour of popovers.
     * @default false
     */
    sticky?: boolean;
    /**
     * Whether to display a backdrop behind the popover
     * @default false
     */
    backdrop?: boolean | React.ReactNode;
} & OverlayProps;

const PositionedOverlay = React.forwardRef(function PositionedOverlay(
    props: PositionedOverlayProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const triggerRef = useOverlayTriggerRef();
    const overlayRef = useObjectRef(ref);
    const arrowRef = React.useRef<SVGSVGElement>(null);
    const closeOverlay = useOverlayClose();
    const {
        offset: offsetValue = 8,
        placement = 'bottom',
        children,
        sticky = false,
        backdrop = false,
        ...domProps
    } = props;

    const [scrollParentRef] = React.useState({
        get current() {
            return triggerRef.current ? getScrollParent(triggerRef.current) : null;
        },
    });

    const {
        update,
        placement: currentPlacement,
        refs,
        floatingStyles,
        context,
        isPositioned,
    } = useFloating({
        transform: false,
        placement,
        whileElementsMounted: autoUpdate,
        middleware: [
            flip({
                crossAxis: placement.includes('-'),
                fallbackAxisSideDirection: 'end',
                padding: offsetValue,
            }),
            offset(offsetValue),
            shift({ padding: offsetValue }),
            arrow({ element: arrowRef }),
        ],
    });

    React.useLayoutEffect(() => {
        refs.setReference(triggerRef.current);
        refs.setFloating(overlayRef.current);
    });

    useEvent(scrollParentRef, 'scroll', sticky ? update : closeOverlay, {
        passive: true,
    });

    // Extract side (top, bottom, left, right)
    const side = currentPlacement.split('-')[0];

    const animatedState = React.useContext(AnimatedStateContext);

    const isExiting = animatedState === 'exiting' && isPositioned;
    const isEntering = animatedState === 'entering' && isPositioned;

    return (
        <>
            {backdrop === true ? (
                <Backdrop isExiting={isExiting} isEntering={isEntering} />
            ) : (
                backdrop
            )}
            <Overlay
                ref={overlayRef}
                className={clsx(
                    'z-[100] fill-mode-forwards',
                    isEntering &&
                        clsx(
                            'fade-in animate-in',
                            'data-[side=top]:slide-in-from-bottom-1',
                            'data-[side=bottom]:slide-in-from-top-1',
                            'data-[side=left]:slide-in-from-right-1',
                            'data-[side=right]:slide-in-from-left-1'
                        ),
                    isExiting &&
                        clsx(
                            'fade-out animate-out duration-[10s]',
                            'data-[side=top]:slide-out-to-bottom-1',
                            'data-[side=bottom]:slide-out-to-top-1',
                            'data-[side=left]:slide-out-to-right-1',
                            'data-[side=right]:slide-out-to-left-1'
                        )
                )}
                data-side={side}
                style={floatingStyles}
                {...domProps}
            >
                <PopoverArrowContext.Provider value={{ ref: arrowRef, context }}>
                    {children}
                </PopoverArrowContext.Provider>
            </Overlay>
        </>
    );
});
